/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';

import { SecurityHelper } from 'sarsaparilla';

import type {
    InteragencyPass,
    PassType,
    PassDetails,
    IdmeVerificationResponse,
} from './interagency-pass-types';

export async function getPass(id: string) {
    const response = await axios.get(`/api/iap/passes/${id}`, {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
    });
    return response.data;
}

export async function deletePass(id: string) {
    const response = await axios.delete(`/api/iap/passes/${id}`);
    return response.data;
}

export async function createPass(
    passType: PassType,
    recaptchaToken?: string | null,
    recaptchaVersion?: string
) {
    const response = await axios.post(
        '/api/iap/passes',
        {
            pass_type: passType,
            recaptcha_token: recaptchaToken,
            recaptcha_version: recaptchaVersion,
        },
        {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
        }
    );
    return response.data;
}

export async function modifyPass(
    passId: string,
    passData: InteragencyPass,
    idVerificationFile?: File | null,
    disabilityVerificationFiles?: File[]
) {
    // If document files are provided, we need to use FormData to send both the files and pass data
    if (
        (idVerificationFile && idVerificationFile.size > 0) ||
        (disabilityVerificationFiles && disabilityVerificationFiles.length > 0)
    ) {
        const formData = new FormData();

        // Append ID verification file if provided
        if (idVerificationFile && idVerificationFile.size > 0) {
            formData.append('files', idVerificationFile);
        }

        // Append disability verification files if provided
        if (disabilityVerificationFiles && disabilityVerificationFiles.length > 0) {
            disabilityVerificationFiles.forEach((file) => {
                formData.append('files', file);
            });
        }

        // Required fields for Document Upload Service
        // TODO: How do we know what to put for document type if there are multiple files?
        formData.append('document_type', idVerificationFile?.type || '');
        formData.append('reservation_id', passId);

        // Append pass data as JSON string
        formData.append('pass_data', JSON.stringify(passData));

        const response = await axios.put(`/api/iap/passes/${passId}`, formData, {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data;
    }

    // If no document files are provided, we can send the pass data as JSON
    const response = await axios.put(`/api/iap/passes/${passId}`, passData, {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
    });

    return response.data;
}

/**
 * Upload documents for access passes
 * @param passId - The pass ID
 * @param fileCategory - The file category (e.g., 'access-identification-verification-file-upload')
 * @param files - The file(s) to upload
 */
export async function uploadAccessPassDocument(
    passId: string,
    fileCategory: string,
    files: File | File[]
): Promise<any> {
    const formData = new FormData();
    const fileArray = Array.isArray(files) ? files : [files];

    // Required fields for Document Upload Service
    formData.append('document_type', fileArray[0].type);
    formData.append('reservation_id', passId);
    formData.append('file_category', fileCategory);

    // Add all files to the form data
    fileArray.forEach((file) => {
        formData.append('files', file);
    });

    const response = await axios.post('/api/iap/upload', formData, {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
            'Content-Type': 'multipart/form-data',
        },
    });

    return response.data;
}

/**
 * Delete a document for access passes
 * @param passId - The pass ID
 * @param documentId - The document ID to delete
 */
export async function deleteAccessPassDocument(
    passId: string,
    documentId: string
): Promise<any> {
    const response = await axios.delete(
        `/api/iap/upload/pass/${passId}/document/${documentId}`,
        {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
        }
    );

    return response.data;
}

// Will be 'any' until Sarsaparilla updates its types
type AccountData = ReturnType<typeof SecurityHelper.getAccount>;

export async function updateAccountInfo(accountData: AccountData) {
    const response = await axios.put('/api/accounts/account', accountData, {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
    });
    return response.data;
}

/**
 * Get all passes for the current user
 * @param signal - AbortSignal for request cancellation
 * @returns Promise<InteragencyPass[]> - Array of user's interagency passes
 */
export async function getUserPasses(
    params?: { is_migrated_pass?: boolean },
    signal?: AbortSignal
): Promise<InteragencyPass[]> {
    const response = await axios.get('/api/iap/passes', {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
        params,
        signal,
    });
    return response.data;
}

export interface PaginatedPassesResponse {
    passes: InteragencyPass[];
    next_cursor: string;
    is_complete: boolean;
}

export interface GetPaginatedPassesParams {
    page_size: number;
    status: 'active' | 'expired';
    cursor?: string;
}

export async function getPaginatedPasses(
    params: GetPaginatedPassesParams,
    signal?: AbortSignal
): Promise<PaginatedPassesResponse> {
    const response = await axios.get('/api/iap/passes/paginated', {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
        params: {
            page_size: params.page_size,
            status: params.status,
            ...(params.cursor && { cursor: params.cursor }),
        },
        signal,
    });
    return response.data;
}

/**
 * Get the list of pass IDs for the current user
 * @param signal - AbortSignal for request cancellation
 * @returns Promise<string[]> - Array of pass IDs
 */
export async function getUserPassIdList(
    status?: 'active' | 'expired',
    signal?: AbortSignal
): Promise<string[]> {
    const response = await axios.get('/api/iap/passes/list', {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
        params: status ? { status } : undefined,
        signal,
    });
    return response.data;
}

/**
 * Get passes by a list of IDs in a single batch request
 * @param ids - Array of pass IDs to fetch
 * @param signal - AbortSignal for request cancellation
 * @returns Promise<InteragencyPass[]> - Array of passes matching the given IDs
 */
export async function getPassesByIds(
    ids: string[],
    signal?: AbortSignal
): Promise<InteragencyPass[]> {
    const response = await axios.post('/api/iap/passes/batch', ids, {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
        signal,
    });
    return response.data;
}

/**
 * Look up a pass by reservation number
 * @param reservationNumber - The external reservation number to search for
 * @param signal - AbortSignal for request cancellation
 * @returns Promise<InteragencyPass> - The pass matching the reservation number
 */
export async function getPassByReservationNumber(
    reservationNumber: string,
    signal?: AbortSignal
): Promise<InteragencyPass> {
    const response = await axios.get(
        `/api/iap/passes/reservation/external/${encodeURIComponent(reservationNumber)}`,
        {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
            signal,
        }
    );
    return response.data;
}

/**
 * Proxy information for Access passes or 4th grade passes.
 * Used when the pass is being connected on behalf of someone else.
 */
export interface ConnectPassProxyFor {
    first_name: string;
    last_name: string;
}

/**
 * Parameters for connecting an existing physical pass.
 */
export interface ConnectPassParams {
    passType: string;
    passNumber: string;
    expiryMonth?: number;
    passTypeVariant?: 'golden';
    proxyFor?: ConnectPassProxyFor;
}

/**
 * Connect an existing physical pass to user's account
 * @param params - Connection parameters including pass type, number, and optional fields
 * @returns Promise with connection result
 */
export async function connectExistingPass(
    params: ConnectPassParams
): Promise<{ success: boolean; passId?: string }> {
    const { passType, passNumber, expiryMonth, passTypeVariant, proxyFor } = params;

    if (!passType || !passNumber) {
        throw new Error('Pass type and pass number are required');
    }

    // Build request payload - only include fields that have values
    const payload: Record<string, unknown> = {
        pass_type: passType,
        pass_number: passNumber,
    };

    // Expiry month is optional (lifetime passes don't have one)
    if (expiryMonth != null) {
        payload.expiry_month = expiryMonth;
    }

    // Pass type variant for golden/historical passes
    if (passTypeVariant != null) {
        payload.pass_type_variant = passTypeVariant;
    }

    // Proxy information for Access or 4th grade passes
    if (proxyFor != null) {
        payload.proxy_for = proxyFor;
    }

    // Make API call
    try {
        const response = await axios.post('/api/iap/passes/connect', payload, {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
        });

        return response.data;
    } catch (error: unknown) {
        // Extract meaningful backend message when possible
        if (axios.isAxiosError(error)) {
            const backendData = error.response?.data as
                { error?: string; message?: string } | undefined;
            const backendMsg = backendData?.error ?? backendData?.message;

            // Re-throw with refined message if available
            if (backendMsg) {
                throw new Error(backendMsg);
            }
        }
        // Fallback: rethrow original error
        throw error;
    }
}

/**
 * Renew an existing interagency pass
 * @param passId - The ID of the pass to renew
 * @returns Promise with new pass ID for renewed pass
 */
export async function renewPass(passId: string): Promise<{ pass_id: string }> {
    const response = await axios.post(
        `/api/iap/passes/${passId}/renew`,
        {},
        {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
        }
    );
    return response.data;
}

/**
 * Downloads a PDF for an interagency pass
 * @param passId - The pass ID to download PDF for
 * @param signal - Optional AbortController signal for cancellation
 * @returns Promise that resolves to a Blob containing the PDF data
 */
export async function downloadPassPdf(
    passId: string,
    signal?: AbortSignal
): Promise<Blob> {
    const response = await axios.get(`/api/iap/passes/${passId}/pdf`, {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
        responseType: 'blob',
        signal,
    });
    return response.data;
}

/**
 * Get pass details for all available pass types
 * @returns Promise<PassDetails[]> - Array of pass details for all available pass types
 */
export async function getPassDetails(): Promise<PassDetails[]> {
    const response = await axios.get('/api/iap/pass-details', {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
    });
    return response.data;
}

// cspell:ignore INTERAGENCYPASS

// ----------------------------------------
// Address Verification
// ----------------------------------------
export interface VerifyAddressInput {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip_code: string;
}

export interface VerifyAddressResponse {
    normalized?: Required<VerifyAddressInput>;
}

export async function verifyAddress(
    address: VerifyAddressInput,
    signal?: AbortSignal
): Promise<VerifyAddressResponse> {
    const response = await axios.post('/api/accounts/address/verify', address, {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
        signal,
    });
    return response.data as VerifyAddressResponse;
}

/**
 * Delete a migrated pass by ID
 * @param passId - The ID of the migrated pass to delete
 * @returns Promise with success status
 */
export async function deleteMigratedPass(passId: string): Promise<{ success: boolean }> {
    const response = await axios.delete(`/api/iap/passes/${passId}/migrated`, {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
    });
    return response.data as { success: boolean };
}

/**
 * Verify pass holder with ID.me
 * @param code - The OAuth authorization code from ID.me
 * @param passId - The ID of the pass to verify
 * @returns Promise with verification result
 */
export async function verifyIdme(
    code: string,
    passId: string
): Promise<IdmeVerificationResponse> {
    const response = await axios.post(
        '/api/iap/verify/idme',
        {
            code,
            pass_id: passId,
        },
        {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
        }
    );
    return response.data;
}

/**
 * Payload for submitting a pass review
 */
export interface SubmitPassReviewPayload {
    action: 'submit';
    review_id: string;
}

/**
 * Submit a pass review for approval
 * Called after documents have been uploaded for an ACTION-REQUIRED pass.
 * This triggers the backend to re-evaluate the pass application.
 * @param passId - The ID of the pass to submit for review
 * @param payload - The review submission payload containing action and review_id
 * @returns Promise with the updated pass data
 */
export async function submitPassReview(
    passId: string,
    payload: SubmitPassReviewPayload
): Promise<InteragencyPass> {
    const response = await axios.put(`/api/iap/passes/reviews/${passId}`, payload, {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
    });
    return response.data;
}

/**
 * Cancel a pass review
 * Called after a user clicks on the "Cancel Application button"
 * @param passId - The ID of the pass to cancel the review for
 * @returns Promise with the updated pass data
 */
export async function cancelPassReview(passId: string): Promise<InteragencyPass> {
    const response = await axios.put(
        `/api/iap/passes/reviews/${passId}`,
        {
            action: 'cancel',
        },
        {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
        }
    );
    return response.data;
}

//cspell:ignore idme
